// src/components/MapView/MapView.jsx
import React, {
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { MapContainer, TileLayer, GeoJSON, useMap } from "react-leaflet";
import { PanelRightOpen } from "lucide-react";
import "leaflet/dist/leaflet.css";

import {
  useMapData,
  useMapSelector,
  useGeoJsonMatcher,
  useTierConfig,
} from "../../hooks";
import {
  initializeFromConfig,
  setSelectedGeography,
  selectSelectedGeographyId,
  selectHighlightedTiers,
  selectMapCenter,
  selectMapZoom,
} from "../../store/mapSlice";

import Header from "./Header";
import Legend from "./Legend";
import Sidebar from "./Sidebar";
import CompetitorMarkers from "./CompetitorMarkers";
import OpportunityMarkers from "./OpportunityMarkers";
import LoadingSpinner from "../UI/LoadingSpinner";

/** Color used for filtered-out (non-highlighted) geographies */
const NO_DATA_COLOR = "#9e9e9e";

/**
 * Component to sync map view with Redux state, clean up tooltips on drag,
 * and suppress tooltip opens triggered by cursor wobble during drag.
 *
 * Also invalidates map size when the desktop sidebar collapses/expands so
 * Leaflet re-measures its container and renders GeoJSON across the full viewport.
 *
 * isDraggingRef: shared ref from MapView. Set to true on dragstart, cleared
 * ~150ms after dragend. While true, mouseover highlighting is skipped and
 * any tooltip that Leaflet tries to open is immediately closed.
 */
const MapController = ({ desktopCollapsed, isDraggingRef }) => {
  const map = useMap();
  const center = useSelector(selectMapCenter);
  const zoom = useSelector(selectMapZoom);

  useEffect(() => {
    if (center && zoom) {
      map.setView(center, zoom);
    }
  }, [map, center, zoom]);

  // When the sidebar collapses/expands, wait for the CSS transition (300ms)
  // then tell Leaflet the container changed size.
  useEffect(() => {
    const timer = setTimeout(() => {
      map.invalidateSize({ animate: false });
    }, 320); // slightly longer than transition-all duration-300
    return () => clearTimeout(timer);
  }, [desktopCollapsed, map]);

  // Drag lifecycle: close tooltips on dragstart, suppress tooltip opens
  // during drag, and keep suppression active briefly after dragend to
  // catch wobble-triggered mouseover events that fire at mouseup.
  useEffect(() => {
    let dragEndTimer = null;

    const onDragStart = () => {
      isDraggingRef.current = true;

      // Close any tooltip that is already open
      map.eachLayer((layer) => {
        if (layer.getTooltip && layer.getTooltip() && layer.isTooltipOpen()) {
          layer.closeTooltip();
        }
      });
    };

    const onDragEnd = () => {
      // Keep the flag raised for a short window so that mouseover events
      // fired by cursor wobble right at mouseup are still suppressed.
      dragEndTimer = setTimeout(() => {
        isDraggingRef.current = false;
      }, 150);
    };

    // Suppress any tooltip that Leaflet tries to open while dragging.
    // This covers both geography tooltips (bindTooltip) and competitor
    // tooltips (react-leaflet <Tooltip>), since both flow through
    // Leaflet's internal tooltip system and fire this event.
    const onTooltipOpen = (e) => {
      if (isDraggingRef.current && e.tooltip && e.tooltip._source) {
        e.tooltip._source.closeTooltip();
      }
    };

    map.on("dragstart", onDragStart);
    map.on("dragend", onDragEnd);
    map.on("tooltipopen", onTooltipOpen);

    return () => {
      map.off("dragstart", onDragStart);
      map.off("dragend", onDragEnd);
      map.off("tooltipopen", onTooltipOpen);
      if (dragEndTimer) clearTimeout(dragEndTimer);
    };
  }, [map, isDraggingRef]);

  return null;
};

/**
 * Compute the style for a feature given its tier, selection, and filter state.
 */
const computeFeatureStyle = (tier, tierColor, isSelected, isFiltered) => {
  if (isSelected) {
    return {
      fillColor: tierColor,
      fillOpacity: 0.85,
      color: "#030712",
      weight: 2.5,
      opacity: 1,
    };
  }

  if (isFiltered) {
    return {
      fillColor: NO_DATA_COLOR,
      fillOpacity: 0.6,
      color: "#bdbdbd",
      weight: 0.3,
      opacity: 0.4,
    };
  }

  return {
    fillColor: tierColor,
    fillOpacity: 0.6,
    color: "#7f8c8d",
    weight: 0.5,
    opacity: 1,
  };
};

/**
 * Main MapView component
 */
const MapView = () => {
  const dispatch = useDispatch();
  const geoJsonRef = useRef(null);

  // Shared dragging ref: true while the user is dragging the map
  // (and for ~150ms after dragend to absorb wobble events).
  const isDraggingRef = useRef(false);

  // Mobile/tablet sidebar state (below lg)
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  // Desktop sidebar collapse state (lg+)
  const [desktopCollapsed, setDesktopCollapsed] = useState(false);

  const { activeState } = useMapSelector();
  const { getTierColor } = useTierConfig();
  const { getGeoIdFromFeature, getGeographyForFeature } = useGeoJsonMatcher();

  const {
    geoJSON,
    loading: dataLoading,
    error: dataError,
    isLoaded,
  } = useMapData();

  const selectedGeographyId = useSelector(selectSelectedGeographyId);
  const highlightedTiers = useSelector(selectHighlightedTiers);
  const mapCenter = useSelector(selectMapCenter);
  const mapZoom = useSelector(selectMapZoom);

  const selectedIdRef = useRef(selectedGeographyId);
  const highlightedTiersRef = useRef(highlightedTiers);

  useEffect(() => {
    selectedIdRef.current = selectedGeographyId;
  }, [selectedGeographyId]);

  useEffect(() => {
    highlightedTiersRef.current = highlightedTiers;
  }, [highlightedTiers]);

  // Auto-open mobile sidebar when a geography is selected (below lg)
  useEffect(() => {
    if (selectedGeographyId) {
      setMobileSidebarOpen(true);
      setDesktopCollapsed(false);
    }
  }, [selectedGeographyId]);

  useEffect(() => {
    if (activeState) {
      dispatch(
        initializeFromConfig({
          mapCenter: activeState.mapCenter,
          defaultZoom: activeState.defaultZoom,
          minZoom: activeState.minZoom,
          maxZoom: activeState.maxZoom,
        }),
      );
    }
  }, [activeState, dispatch]);

  useEffect(() => {
    const layerGroup = geoJsonRef.current;
    if (!layerGroup) return;

    layerGroup.eachLayer((layer) => {
      const feature = layer.feature;
      if (!feature) return;

      const geography = getGeographyForFeature(feature);
      const geoId = getGeoIdFromFeature(feature);
      const tier = geography?.tier;
      const tierColor = tier ? getTierColor(tier) : NO_DATA_COLOR;
      const isSelected = geoId === selectedGeographyId;
      const isFiltered =
        highlightedTiers.length > 0 &&
        tier &&
        !highlightedTiers.includes(tier.toLowerCase());

      layer.setStyle(
        computeFeatureStyle(tier, tierColor, isSelected, isFiltered),
      );
    });
  }, [
    selectedGeographyId,
    highlightedTiers,
    getGeographyForFeature,
    getGeoIdFromFeature,
    getTierColor,
  ]);

  const getFeatureStyle = useCallback(
    (feature) => {
      const geography = getGeographyForFeature(feature);
      const tier = geography?.tier;
      const tierColor = tier ? getTierColor(tier) : NO_DATA_COLOR;

      return {
        fillColor: tierColor,
        fillOpacity: 0.6,
        color: "#7f8c8d",
        weight: 0.5,
        opacity: 1,
      };
    },
    [getGeographyForFeature, getTierColor],
  );

  const onEachFeature = useCallback(
    (feature, layer) => {
      const geography = getGeographyForFeature(feature);
      const geoId = getGeoIdFromFeature(feature);

      layer.on({
        click: () => {
          if (geoId) {
            dispatch(setSelectedGeography(geoId));
          }
        },
        mouseover: (e) => {
          // Skip hover highlighting while the user is dragging (or in
          // the brief post-drag window). This prevents wobble-triggered
          // mouseover events from altering layer styles.
          if (isDraggingRef.current) return;

          const targetLayer = e.target;
          const currentSelected = selectedIdRef.current;
          const currentHighlighted = highlightedTiersRef.current;
          const tier = geography?.tier;
          const isSelected = geoId === currentSelected;
          const isFiltered =
            currentHighlighted.length > 0 &&
            tier &&
            !currentHighlighted.includes(tier.toLowerCase());

          if (!isFiltered || isSelected) {
            targetLayer.setStyle({
              weight: 2.5,
              fillOpacity: 0.85,
            });
          }
        },
        mouseout: (e) => {
          const targetLayer = e.target;
          const currentSelected = selectedIdRef.current;
          const currentHighlighted = highlightedTiersRef.current;
          const tier = geography?.tier;
          const tierColor = tier ? getTierColor(tier) : NO_DATA_COLOR;
          const isSelected = geoId === currentSelected;
          const isFiltered =
            currentHighlighted.length > 0 &&
            tier &&
            !currentHighlighted.includes(tier.toLowerCase());

          targetLayer.setStyle(
            computeFeatureStyle(tier, tierColor, isSelected, isFiltered),
          );
        },
      });

      if (geography) {
        const city = geography.city || "Unknown";
        const zcta = geography.zcta || geography.geoId || "";
        const score = geography.score;
        const tier = geography.tier || "No data";

        layer.bindTooltip(
          `
        <div style="min-width: 120px; max-width: 240px;">
          <strong style="display: block; word-wrap: break-word; overflow-wrap: break-word;">${city}</strong>
          <div style="color: #666; font-size: 11px; word-wrap: break-word; overflow-wrap: break-word;">${zcta}</div>
          <div style="margin-top: 4px; display: flex; justify-content: space-between; align-items: center;">
            <span style="font-size: 11px;">${tier}</span>
            <span style="font-weight: bold;">${score ?? "N/A"}</span>
          </div>
        </div>
      `,
          {
            permanent: false,
            direction: "top",
            className: "map-tooltip",
            offset: [0, -5],
            sticky: true,
          },
        );
      } else {
        const geoIdDisplay = getGeoIdFromFeature(feature);
        layer.bindTooltip(
          `<div style="min-width: 120px; max-width: 240px;"><strong style="display: block; word-wrap: break-word; overflow-wrap: break-word;">${geoIdDisplay || "Unknown"}</strong><br/><span style="color: #999;">No data</span></div>`,
          {
            permanent: false,
            direction: "top",
            offset: [0, -5],
            sticky: true,
          },
        );
      }
    },
    [getGeographyForFeature, getGeoIdFromFeature, getTierColor, dispatch],
  );

  const geoJsonKey = useMemo(() => {
    const featureCount = geoJSON?.features?.length || 0;
    return `geojson-${featureCount}`;
  }, [geoJSON]);

  if (dataLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner size="large" message="Loading map data..." />
      </div>
    );
  }

  if (dataError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">
            Error Loading Data
          </h2>
          <p className="text-gray-600 mb-4">{dataError}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-secondary text-white rounded-lg hover:bg-secondary-dark transition"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      <Header />

      <div className="flex-1 flex overflow-hidden relative">
        <div className="flex-1 relative">
          <MapContainer
            center={mapCenter}
            zoom={mapZoom}
            className="h-full w-full"
            zoomControl={true}
            scrollWheelZoom={true}
            minZoom={activeState?.minZoom || 7}
            maxZoom={activeState?.maxZoom || 18}
          >
            <MapController
              desktopCollapsed={desktopCollapsed}
              isDraggingRef={isDraggingRef}
            />

            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            {geoJSON && isLoaded && geoJSON.features && (
              <GeoJSON
                key={geoJsonKey}
                ref={geoJsonRef}
                data={geoJSON}
                style={getFeatureStyle}
                onEachFeature={onEachFeature}
              />
            )}

            {/* Competitor pins (z-500) and opportunity pins (z-510) rendered independently */}
            {isLoaded && <CompetitorMarkers />}
            {isLoaded && <OpportunityMarkers />}
          </MapContainer>

          <Legend />

          {/* Mobile/tablet sidebar toggle — visible below lg */}
          {!mobileSidebarOpen && (
            <button
              onClick={() => setMobileSidebarOpen(true)}
              className="lg:hidden fixed bottom-6 right-4 z-[1000] bg-white shadow-lg rounded-full p-3.5 text-gray-700 hover:bg-gray-50 active:bg-gray-100 transition"
              aria-label="Open sidebar"
            >
              <PanelRightOpen size={22} />
            </button>
          )}
        </div>

        {/* Sidebar */}
        <Sidebar
          mobileOpen={mobileSidebarOpen}
          onMobileClose={() => setMobileSidebarOpen(false)}
          desktopCollapsed={desktopCollapsed}
          onDesktopToggle={() => setDesktopCollapsed((c) => !c)}
        />
      </div>
    </div>
  );
};

export default MapView;
